/**
 * SEO Checker function that analyzes HTML content for various SEO factors
 * @param {string} html - The HTML content to analyze
 * @param {string} keyword - The target keyword to check for
 * @returns {Object} Results object containing overall score and individual checks
 */
export const seoChecker = (html, keyword) => {
  // Initialize the results object
  const results = {
    overall_score: 0,
    checks: []
  };

  // Helper function to add check results
  const addCheck = (topic, score, commentary) => {
    results.checks.push({
      topic,
      score,
      commentary
    });
  };

  // 1. Title Tag Check
  const titleCheck = () => {
    const titleScore = {
      hasKeyword: 0,
      properLength: 0,
      keywordPosition: 0
    };
    
    const title = html.match(/<title>(.*?)<\/title>/i)?.[1] || "";
    titleScore.hasKeyword = title.toLowerCase().includes(keyword.toLowerCase()) ? 100 : 0;
    
    titleScore.properLength = (title.length >= 50 && title.length <= 60) ? 100 : 
                             (title.length > 30 && title.length < 70) ? 70 : 40;
    
    titleScore.keywordPosition = title.toLowerCase().startsWith(keyword.toLowerCase()) ? 100 : 
                                title.toLowerCase().indexOf(keyword.toLowerCase()) < 30 ? 70 : 40;
    
    const averageScore = Math.round((titleScore.hasKeyword + titleScore.properLength + titleScore.keywordPosition) / 3);
    
    addCheck(
      "Title Tag Optimization",
      averageScore,
      `Title ${title ? 'exists' : 'missing'}. Length: ${title.length} chars. ` +
      `Keyword ${titleScore.hasKeyword ? 'present' : 'missing'}. ` +
      `Position: ${titleScore.keywordPosition === 100 ? 'optimal' : 'could be improved'}.`
    );
  };

  // 2. Meta Description Check
  const metaDescriptionCheck = () => {
    const metaDesc = html.match(/<meta\s+name="description"\s+content="([^"]*)">/i)?.[1] || "";
    const score = {
      exists: metaDesc ? 100 : 0,
      hasKeyword: metaDesc.toLowerCase().includes(keyword.toLowerCase()) ? 100 : 0,
      properLength: (metaDesc.length >= 120 && metaDesc.length <= 160) ? 100 : 
                   (metaDesc.length >= 80 && metaDesc.length <= 180) ? 70 : 40
    };
    
    const averageScore = Math.round((score.exists + score.hasKeyword + score.properLength) / 3);
    
    addCheck(
      "Meta Description",
      averageScore,
      `Description ${metaDesc ? 'exists' : 'missing'}. Length: ${metaDesc.length} chars. ` +
      `Keyword ${score.hasKeyword ? 'present' : 'missing'}.`
    );
  };

  // 3. Header Tags Hierarchy Check
  const headerCheck = () => {
    const h1Count = (html.match(/<h1[^>]*>/g) || []).length;
    const h2Count = (html.match(/<h2[^>]*>/g) || []).length;
    const h3Count = (html.match(/<h3[^>]*>/g) || []).length;
    
    const score = {
      h1Presence: h1Count === 1 ? 100 : h1Count === 0 ? 0 : 50,
      h2Presence: h2Count >= 2 && h2Count <= 6 ? 100 : h2Count > 0 ? 70 : 0,
      keywordInH1: html.toLowerCase().includes(`<h1[^>]*>${keyword.toLowerCase()}</h1>`) ? 100 : 0
    };
    
    const averageScore = Math.round((score.h1Presence + score.h2Presence + score.keywordInH1) / 3);
    
    addCheck(
      "Header Tags Structure",
      averageScore,
      `H1 tags: ${h1Count} (should be 1). H2 tags: ${h2Count}. H3 tags: ${h3Count}. ` +
      `Keyword in H1: ${score.keywordInH1 ? 'yes' : 'no'}.`
    );
  };

  // 4. Content Quality Check
  const contentCheck = () => {
    const content = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const wordCount = content.split(' ').length;
    const sentences = content.split(/[.!?]+/).length;
    const avgWordsPerSentence = wordCount / sentences;
    const paragraphs = html.match(/<p[^>]*>/g)?.length || 0;
    const keywordDensity = (content.toLowerCase().match(new RegExp(keyword.toLowerCase(), 'g')) || []).length;
    const densityPercentage = (keywordDensity / wordCount) * 100;
    
    const score = {
      contentLength: wordCount >= 800 ? 100 : wordCount >= 500 ? 70 : 40,
      keywordDensity: (densityPercentage >= 0.5 && densityPercentage <= 2.5) ? 100 : 
                     (densityPercentage > 0 && densityPercentage <= 4) ? 70 : 40,
      readability: (avgWordsPerSentence >= 15 && avgWordsPerSentence <= 20) ? 100 :
                  (avgWordsPerSentence >= 10 && avgWordsPerSentence <= 25) ? 70 : 40,
      structure: (paragraphs >= 5) ? 100 : (paragraphs >= 3) ? 70 : 40
    };
    
    const averageScore = Math.round(Object.values(score).reduce((a, b) => a + b) / Object.keys(score).length);
    
    addCheck(
      "Content Quality",
      averageScore,
      `Word count: ${wordCount}. ` +
      `Keyword density: ${densityPercentage.toFixed(2)}%. ` +
      `Average sentence length: ${avgWordsPerSentence.toFixed(1)} words. ` +
      `Paragraphs: ${paragraphs}. ` +
      `Content structure is ${score.structure === 100 ? 'optimal' : 'could be improved'}.`
    );
  };

  // 5. Image Optimization Check
  const imageCheck = () => {
    const images = html.match(/<img[^>]*>/g) || [];
    let altTextCount = 0;
    let keywordInAlt = 0;
    
    images.forEach(img => {
      if (img.includes('alt="')) altTextCount++;
      if (img.toLowerCase().includes(keyword.toLowerCase())) keywordInAlt++;
    });
    
    const score = {
      hasImages: images.length > 0 ? 100 : 0,
      altTextPresence: images.length > 0 ? (altTextCount / images.length) * 100 : 0,
      keywordInAlt: keywordInAlt > 0 ? 100 : 0
    };
    
    const averageScore = Math.round((score.hasImages + score.altTextPresence + score.keywordInAlt) / 3);
    
    addCheck(
      "Image Optimization",
      averageScore,
      `Images found: ${images.length}. Alt text present: ${altTextCount}/${images.length}. ` +
      `Keyword in alt text: ${keywordInAlt > 0 ? 'yes' : 'no'}.`
    );
  };

  // 6. Schema Markup Check
  const schemaCheck = () => {
    const hasSchema = html.includes('application/ld+json') || html.includes('itemtype="http://schema.org');
    const hasLocalBusiness = html.includes('LocalBusiness') || html.includes('Organization');
    
    const score = {
      schemaPresence: hasSchema ? 100 : 0,
      localBusiness: hasLocalBusiness ? 100 : 0
    };
    
    const averageScore = Math.round((score.schemaPresence + score.localBusiness) / 2);
    
    addCheck(
      "Schema Markup",
      averageScore,
      `Schema markup ${hasSchema ? 'present' : 'missing'}. ` +
      `Local Business schema ${hasLocalBusiness ? 'implemented' : 'missing'}.`
    );
  };

  // 7. Mobile Responsiveness Check
  const mobileCheck = () => {
    const hasViewport = html.includes('<meta name="viewport"');
    const hasMediaQueries = html.includes('@media');
    const hasTouchIcons = html.includes('apple-touch-icon') || html.includes('msapplication-tile');
    const hasAmp = html.includes('<link rel="amphtml"');
    
    const score = {
      viewport: hasViewport ? 100 : 0,
      mediaQueries: hasMediaQueries ? 100 : 0,
      touchIcons: hasTouchIcons ? 100 : 0,
      ampSupport: hasAmp ? 100 : 50
    };
    
    const averageScore = Math.round(Object.values(score).reduce((a, b) => a + b) / Object.keys(score).length);
    
    addCheck(
      "Mobile Optimization",
      averageScore,
      `Viewport meta: ${hasViewport ? 'present' : 'missing'}. ` +
      `Media queries: ${hasMediaQueries ? 'implemented' : 'missing'}. ` +
      `Touch icons: ${hasTouchIcons ? 'present' : 'missing'}. ` +
      `AMP support: ${hasAmp ? 'yes' : 'no'}.`
    );
  };

  // 8. Local SEO Elements Check
  const localSEOCheck = () => {
    const hasAddress = html.includes('address') || html.includes('postal-code');
    const hasPhone = html.includes('tel:') || /[\d-+()]{10,}/.test(html);
    const hasMap = html.includes('maps.google') || html.includes('iframe');
    
    const score = {
      address: hasAddress ? 100 : 0,
      phone: hasPhone ? 100 : 0,
      map: hasMap ? 100 : 0
    };
    
    const averageScore = Math.round((score.address + score.phone + score.map) / 3);
    
    addCheck(
      "Local SEO Elements",
      averageScore,
      `Business address ${hasAddress ? 'found' : 'missing'}. ` +
      `Phone number ${hasPhone ? 'present' : 'missing'}. ` +
      `Map integration ${hasMap ? 'present' : 'missing'}.`
    );
  };

  // Add new URL Structure Check
  const urlCheck = () => {
    // Get both the canonical URL and the input URL
    const canonicalUrl = html.match(/<link[^>]*canonical[^>]*href="([^"]*)">/i)?.[1] || "";
    const hasCanonical = !!canonicalUrl;
    
    // Check SSL using the input URL (keyword parameter is actually the URL in this context)
    const hasSSL = keyword.toLowerCase().startsWith('https://');
    const isClean = !/[^a-zA-Z0-9-_\/\.]/.test(keyword);
    
    const score = {
      hasCanonical: hasCanonical ? 100 : 0,
      hasSSL: hasSSL ? 100 : 0,
      cleanStructure: isClean ? 100 : 50
    };
    
    const averageScore = Math.round((score.hasCanonical + score.hasSSL + score.cleanStructure) / 3);
    
    addCheck(
      "URL Optimization",
      averageScore,
      `Canonical URL ${hasCanonical ? 'present' : 'missing'}. ` +
      `SSL ${hasSSL ? 'enabled' : 'missing'}. ` +
      `URL structure is ${isClean ? 'clean' : 'could be improved'}.`
    );
  };

  // Add Social Media Meta Tags Check
  const socialCheck = () => {
    const ogTitle = html.match(/<meta[^>]*og:title[^>]*content="([^"]*)">/i)?.[1];
    const ogDesc = html.match(/<meta[^>]*og:description[^>]*content="([^"]*)">/i)?.[1];
    const ogImage = html.match(/<meta[^>]*og:image[^>]*content="([^"]*)">/i)?.[1];
    const twitterCard = html.match(/<meta[^>]*twitter:card[^>]*content="([^"]*)">/i)?.[1];
    
    const score = {
      openGraph: (ogTitle && ogDesc && ogImage) ? 100 : 
                 (ogTitle || ogDesc || ogImage) ? 50 : 0,
      twitter: twitterCard ? 100 : 0
    };
    
    const averageScore = Math.round((score.openGraph + score.twitter) / 2);
    
    addCheck(
      "Social Media Optimization",
      averageScore,
      `Open Graph tags ${score.openGraph === 100 ? 'fully implemented' : 
                        score.openGraph === 50 ? 'partially implemented' : 
                        'missing'}. ` +
      `Twitter Card ${score.twitter === 100 ? 'present' : 'missing'}.`
    );
  };

  // Add Performance Indicators Check
  const performanceCheck = () => {
    const hasLazyLoading = html.includes('loading="lazy"');
    const hasDeferredJS = html.includes('defer') || html.includes('async');
    const cssCount = (html.match(/<link[^>]*stylesheet[^>]*>/g) || []).length;
    const jsCount = (html.match(/<script[^>]*>/g) || []).length;
    
    const score = {
      lazyLoading: hasLazyLoading ? 100 : 0,
      deferredLoading: hasDeferredJS ? 100 : 0,
      resourceCount: (cssCount <= 3 && jsCount <= 5) ? 100 :
                    (cssCount <= 5 && jsCount <= 8) ? 70 : 40
    };
    
    const averageScore = Math.round(Object.values(score).reduce((a, b) => a + b) / Object.keys(score).length);
    
    addCheck(
      "Performance Optimization",
      averageScore,
      `Lazy loading ${hasLazyLoading ? 'implemented' : 'missing'}. ` +
      `Deferred loading ${hasDeferredJS ? 'implemented' : 'missing'}. ` +
      `Resource count: ${cssCount} CSS, ${jsCount} JS files.`
    );
  };

  // Add Internal Linking Check
  const internalLinkCheck = () => {
    const links = html.match(/<a[^>]*href="([^"]*)"[^>]*>/g) || [];
    const internalLinks = links.filter(link => !link.includes('http') || link.includes(document.domain));
    const hasKeywordInLinks = links.some(link => 
      link.toLowerCase().includes(keyword.toLowerCase())
    );
    
    const score = {
      linkCount: internalLinks.length >= 3 ? 100 :
                 internalLinks.length > 0 ? 70 : 0,
      keywordInLinks: hasKeywordInLinks ? 100 : 0
    };
    
    const averageScore = Math.round((score.linkCount + score.keywordInLinks) / 2);
    
    addCheck(
      "Internal Linking",
      averageScore,
      `Internal links: ${internalLinks.length}. ` +
      `Keyword in link text: ${hasKeywordInLinks ? 'yes' : 'no'}.`
    );
  };

  // Run all checks
  titleCheck();
  metaDescriptionCheck();
  headerCheck();
  contentCheck();
  imageCheck();
  schemaCheck();
  mobileCheck();
  localSEOCheck();
  urlCheck();
  socialCheck();
  performanceCheck();
  internalLinkCheck();

  // Calculate overall score
  results.overall_score = Math.round(
    results.checks.reduce((acc, check) => acc + check.score, 0) / results.checks.length
  );

  return results;
};

// Export example usage function
export const runSEOCheck = (htmlContent, keyword) => {
  return seoChecker(htmlContent, keyword);
}; 